import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import { useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, Heart, Share, MoreHorizontal } from 'lucide-react'
import { useMusic } from '../contexts/MusicContext'
import MusicPlayer from '../components/MusicPlayer'
import PlaylistView from '../components/PlaylistView'
import WeatherWidget from '../components/WeatherWidget'
import DebugPanel from '../components/DebugPanel'
import { getMusicRecommendations } from '../services/musicService'
import { getWeatherMoodMapping } from '../services/weatherService'

const PlayerPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { 
    currentTrack, 
    playlist, 
    weather, 
    location: userLocation,
    dispatch,
    setPlaylist,
    setCurrentTrack
  } = useMusic()
  
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const state = location.state
    
    if (state?.mood || state?.searchQuery) {
      loadMusic(state)
    } else if (playlist.length === 0) {
      // 如果没有传递状态且没有播放列表，返回首页
      navigate('/')
    } else {
      setIsLoading(false)
    }
  }, [location.state])

  const loadMusic = async (state) => {
    setIsLoading(true)
    setError(null)

    try {
      let musicData
      
      if (state.isSearch && state.searchQuery) {
        // 搜索模式
        musicData = await getMusicRecommendations({
          query: state.searchQuery,
          type: 'search'
        })
      } else if (state.mood) {
        // 心情推荐模式
        const weatherMoods = weather ? getWeatherMoodMapping(weather) : []
        const combinedMoods = [state.mood, ...weatherMoods]
        
        musicData = await getMusicRecommendations({
          mood: state.mood,
          weather: weather,
          location: userLocation,
          additionalMoods: weatherMoods,
          type: 'mood'
        })
      }

      if (musicData && musicData.length > 0) {
        setPlaylist(musicData)
        setCurrentTrack(musicData[0])
        
        // 保存推荐参数
        dispatch({ 
          type: 'SET_MOOD', 
          payload: state.mood || state.searchQuery 
        })
      } else {
        setError('未找到相关音乐，请尝试其他心情或搜索词')
      }
    } catch (error) {
      console.error('Error loading music:', error)
      setError('加载音乐时出错，请稍后重试')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">
            正在为你寻找音乐
          </h2>
          <p className="text-gray-500">
            基于你的心情和当前天气...
          </p>
        </motion.div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md"
        >
          <div className="text-6xl mb-4">😔</div>
          <h2 className="text-xl font-semibold text-gray-700 mb-4">
            {error}
          </h2>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-600 text-white rounded-full font-medium hover:from-purple-600 hover:to-blue-700 transition-all duration-200 shadow-lg"
          >
            返回首页
          </motion.button>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen pb-24">
      {/* 顶部导航栏 */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sticky top-0 z-10 bg-white bg-opacity-80 backdrop-blur-sm border-b border-gray-200"
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-800"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>返回</span>
            </motion.button>

            <WeatherWidget weather={weather} location={userLocation} />

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full"
            >
              <MoreHorizontal className="w-5 h-5" />
            </motion.button>
          </div>
        </div>
      </motion.header>

      {/* 主要内容区域 */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* 播放器区域 */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="lg:col-span-2"
          >
            {currentTrack && (
              <MusicPlayer 
                track={currentTrack}
                onNext={() => dispatch({ type: 'NEXT_TRACK' })}
                onPrevious={() => dispatch({ type: 'PREVIOUS_TRACK' })}
              />
            )}
          </motion.div>

          {/* 播放列表区域 */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="lg:col-span-1"
          >
            <PlaylistView 
              playlist={playlist}
              currentTrack={currentTrack}
              onTrackSelect={setCurrentTrack}
            />
          </motion.div>
        </div>
      </div>

      {/* 底部迷你播放器（移动端） */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="fixed bottom-0 left-0 right-0 lg:hidden bg-white border-t border-gray-200 p-4"
      >
        {currentTrack && (
          <div className="flex items-center space-x-4">
            <img
              src={currentTrack.artwork || '/default-cover.jpg'}
              alt={currentTrack.title}
              className="w-12 h-12 rounded-lg object-cover"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {currentTrack.title}
              </p>
              <p className="text-sm text-gray-500 truncate">
                {currentTrack.artist}
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="p-2 text-gray-600 hover:text-red-500"
            >
              <Heart className="w-5 h-5" />
            </motion.button>
          </div>
        )}
      </motion.div>

      {/* 开发调试面板 */}
      <DebugPanel />
    </div>
  )
}

export default PlayerPage
